/**
 * Shared utilities for Power BI report embed components.
 */

/** Embed info for a single Power BI report returned from the API. */
export interface ReportEmbedEntry {
  embedUrl: string;
  accessToken: string;
  reportId: string;
  reportName?: string;
}

type ReportResults = Array<{
  embedReports?: Array<{ reportId?: string; embedUrl?: string; reportName?: string }>;
  embedToken?: { token?: string };
}>;

/** Build query string from rp:* params (same format as Inquiry reports). */
export function buildReportParamsQueryString(reportParams: Record<string, string | string[]>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(reportParams)) {
    if (key == null || value == null) continue;
    const values = Array.isArray(value) ? value : [value];
    for (const v of values) {
      if (v != null && v !== '') {
        // Use the key as-is: rp:* keys contain a colon which must NOT be percent-encoded
        // (Power BI rdlEmbed expects literal rp:paramName, not rp%3AparamName)
        parts.push(key + '=' + encodeURIComponent(String(v)));
      }
    }
  }
  return parts.join('&');
}

/** Clears and re-populates a reportId → ReportEmbedEntry map from backend results. */
export function populateReportConfigMap(
  map: Map<string, ReportEmbedEntry>,
  results: ReportResults
): void {
  map.clear();
  for (const item of results) {
    if (!item.embedReports?.length || !item.embedToken?.token) continue;
    for (const r of item.embedReports) {
      if (r.reportId && r.embedUrl) {
        map.set(r.reportId, {
          reportId: r.reportId,
          embedUrl: r.embedUrl,
          accessToken: item.embedToken.token,
          reportName: r.reportName
        });
      }
    }
  }
}

/** Find report in map by id (exact, case-insensitive, embedUrl contains, then typo fallback). */
export function findReportEntryById(
  map: Map<string, ReportEmbedEntry>,
  reportId: string
): ReportEmbedEntry | undefined {
  const entry = map.get(reportId);
  if (entry) return entry;
  const normalized = reportId?.trim().toLowerCase();
  if (!normalized) return undefined;
  for (const [, value] of map) {
    if (value.reportId?.trim().toLowerCase() === normalized) return value;
  }
  for (const [, value] of map) {
    if (value.embedUrl?.toLowerCase().includes(normalized)) return value;
  }
  // Fallback: requested id may have an extra trailing character (e.g. ...81279 vs actual ...8127)
  if (reportId.length > 1) {
    const withoutLastChar = reportId.slice(0, -1);
    const typoEntry = map.get(withoutLastChar)
      ?? Array.from(map.values()).find(
          v => v.reportId?.trim().toLowerCase() === withoutLastChar.trim().toLowerCase()
        );
    if (typoEntry) return typoEntry;
  }
  return undefined;
}

/** Read rp:* params from current URL, falling back to sessionStorage when URL has been stripped (e.g. after OAuth redirect). */
export function getReportParamsFromUrlOrStorage(): Record<string, string | string[]> | undefined {
  const loc = (globalThis as any).location;
  const search = typeof loc?.search === 'string' ? loc.search : '';
  if (search) {
    const sp = new URLSearchParams(search);
    const fromUrl: Record<string, string | string[]> = {};
    for (const key of sp.keys()) {
      if (key.startsWith('rp:')) {
        // Deduplicate values — same key can appear multiple times due to DAX measure behaviour.
        const unique = [...new Set(sp.getAll(key))];
        fromUrl[key] = unique.length === 1 ? unique[0] : unique;
      }
    }
    if (Object.keys(fromUrl).length > 0) return fromUrl;
  }
  return undefined;
}